import { IndianRupee, CreditCard, CheckCircle, Clock, AlertCircle, X, AlertTriangle, Activity } from 'lucide-react';
import React from 'react';

export function useBillingUtils() {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status: string | null | undefined) => {
    // Safety check - ensure status is a string
    if (!status || typeof status !== 'string') {
      return {
        label: 'Unknown',
        className: '',
        icon: React.createElement(Activity, { className: "w-3 h-3" }),
        color: 'text-muted-foreground',
        bgColor: 'bg-muted',
        textColor: 'text-muted-foreground',
        borderColor: 'border-border'
      };
    }

    switch (status.toLowerCase()) {
      case 'paid':
        return {
          label: 'Paid',
          className: 'status-active',
          icon: React.createElement(CheckCircle, { className: "w-3 h-3" }),
          color: 'text-success',
          bgColor: 'bg-success/10',
          textColor: 'text-success',
          borderColor: 'border-success/20'
        };
      case 'pending':
        return {
          label: 'Pending',
          className: 'status-warning',
          icon: React.createElement(Clock, { className: "w-3 h-3" }),
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          textColor: 'text-warning',
          borderColor: 'border-warning/20'
        };
      case 'partial':
        return {
          label: 'Partial',
          className: 'status-critical',
          icon: React.createElement(AlertCircle, { className: "w-3 h-3" }),
          color: 'text-destructive',
          bgColor: 'bg-primary/10',
          textColor: 'text-primary',
          borderColor: 'border-primary/20'
        };
      case 'cancelled':
      case 'canceled':
        return {
          label: 'Cancelled',
          className: 'status-critical',
          icon: React.createElement(X, { className: "w-3 h-3" }),
          color: 'text-destructive',
          bgColor: 'bg-destructive/10',
          textColor: 'text-destructive',
          borderColor: 'border-destructive/20'
        };
      case 'overdue':
        return {
          label: 'Overdue',
          className: 'status-warning',
          icon: React.createElement(AlertTriangle, { className: "w-3 h-3" }),
          color: 'text-warning',
          bgColor: 'bg-destructive/10',
          textColor: 'text-destructive',
          borderColor: 'border-destructive/20'
        };
      default:
        return {
          label: status,
          className: '',
          icon: React.createElement(Activity, { className: "w-3 h-3" }),
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          textColor: 'text-muted-foreground',
          borderColor: 'border-border'
        };
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'cash': return React.createElement(IndianRupee, { className: "w-4 h-4" });
      case 'upi': return React.createElement(CreditCard, { className: "w-4 h-4" });
      case 'card': return React.createElement(CreditCard, { className: "w-4 h-4" });
      case 'bank transfer': return React.createElement(CreditCard, { className: "w-4 h-4" });
      default: return React.createElement(CreditCard, { className: "w-4 h-4" });
    }
  };

  const calculateBillingSummary = (invoices: any[]) => {
    const totalRevenue = invoices
      .filter(inv => inv.payment_status === 'Paid')
      .reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    
    const pendingAmount = invoices
      .filter(inv => inv.payment_status === 'Pending' || inv.payment_status === 'Partial')
      .reduce((sum, inv) => sum + (inv.balance_amount || 0), 0);
    
    const totalInvoices = invoices.length;
    const paidInvoices = invoices.filter(inv => inv.payment_status === 'Paid').length;
    const pendingInvoices = invoices.filter(inv => inv.payment_status !== 'Paid').length;

    return {
      totalRevenue,
      pendingAmount,
      totalInvoices,
      paidInvoices,
      pendingInvoices
    };
  };

  return {
    formatCurrency,
    formatDate,
    getStatusConfig,
    getPaymentMethodIcon,
    calculateBillingSummary
  };
} 